import { Link, usePage } from "@inertiajs/react"
import { Dices } from "lucide-react"
import type { ReactNode } from "react"

import AppLogoIcon from "@/components/app-logo-icon"
import AppearanceToggleDropdown from "@/components/appearance-dropdown"
import { decadeLabel } from "@/components/directory/decade-badge"
import PersistentLayout from "@/layouts/persistent-layout"
import { cn } from "@/lib/utils"
import {
  aboutPath,
  adminRootPath,
  randomPath,
  rootPath,
  submitPath,
} from "@/routes"
import type { SharedData } from "@/types"

const NAV = [
  { title: "Directory", href: rootPath() },
  { title: "Submit", href: submitPath() },
  { title: "About", href: aboutPath() },
]

export default function PublicLayout({ children }: { children: ReactNode }) {
  const page = usePage<SharedData>()
  const path = page.url.split("?")[0]
  const user = page.props.auth.user as SharedData["auth"]["user"] | null

  return (
    <div className="flex min-h-screen flex-col">
      <header className="bg-background/85 sticky top-0 z-40 border-b backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-3 px-4 sm:gap-6 sm:px-6">
          <Link
            href={rootPath()}
            className="flex items-center gap-2 font-bold tracking-tight"
          >
            <span className="bg-brand flex size-7 items-center justify-center rounded-md text-white">
              <AppLogoIcon className="size-5" />
            </span>
            <span className="hidden sm:inline">SSS Directory</span>
          </Link>
          <nav className="flex items-center gap-0.5 text-sm sm:gap-1">
            {NAV.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "rounded-md px-2 py-1.5 font-medium transition-colors sm:px-3",
                  (
                    item.href === "/"
                      ? path === "/"
                      : path.startsWith(item.href)
                  )
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {item.title}
              </Link>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            {user?.admin && (
              <Link
                href={adminRootPath()}
                className="text-muted-foreground hover:text-foreground hidden text-sm font-medium sm:inline"
              >
                Admin
              </Link>
            )}
            <AppearanceToggleDropdown className="hidden sm:block" />
            <a
              href={randomPath()}
              className="bg-brand hover:bg-brand-hover inline-flex items-center gap-2 rounded-full p-2.5 text-sm font-semibold text-white transition-transform active:scale-95 sm:px-4 sm:py-2"
              aria-label="Visit a random site"
            >
              <Dices className="size-4" />
              <span className="hidden sm:inline">Random</span>
            </a>
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="mt-24 border-t">
        <div className="mx-auto grid max-w-[1400px] gap-10 px-4 py-12 text-sm sm:grid-cols-[2fr_1fr_1fr] sm:px-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2 font-bold">
              <span className="bg-brand flex size-6 items-center justify-center rounded text-white">
                <AppLogoIcon className="size-4" />
              </span>
              Single Serving Sites Directory
            </div>
            <p className="text-muted-foreground max-w-md">
              One site, one purpose. Every entry is screenshotted by a headless
              browser and checked every hour. When one goes dark, we keep its
              memory and point you to the Wayback Machine.
            </p>
            <p className="text-muted-foreground">
              Seeded from{" "}
              <a
                className="text-foreground underline underline-offset-4"
                href="https://singleservingsites.cool/"
              >
                singleservingsites.cool
              </a>
              , huphtur&apos;s hand-curated list.
            </p>
          </div>
          <div>
            <h4 className="mb-3 font-semibold">By decade</h4>
            <ul className="text-muted-foreground space-y-2">
              {[1990, 2000, 2010, 2020].map((decade) => (
                <li key={decade}>
                  <Link
                    href={rootPath({ decade })}
                    className="hover:text-foreground"
                  >
                    {decadeLabel(decade)} sites
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="mb-3 font-semibold">More</h4>
            <ul className="text-muted-foreground space-y-2">
              <li>
                <Link href={submitPath()} className="hover:text-foreground">
                  Submit a site
                </Link>
              </li>
              <li>
                <Link href={aboutPath()} className="hover:text-foreground">
                  What is a single-serving site?
                </Link>
              </li>
              <li>
                <a href="/api/v1/sites" className="hover:text-foreground">
                  JSON API
                </a>
              </li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  )
}

// Persistent across page visits, so the header does not remount.
export const withPublicLayout = (page: ReactNode) => (
  <PersistentLayout>
    <PublicLayout>{page}</PublicLayout>
  </PersistentLayout>
)
